import { useErrorHandlingMutation } from '@backoffice-frontend/data-fetching'
import {
  Body1,
  Divider,
  MoiaDialog,
  MoiaDialogContent,
  MoiaDialogFormFooter,
  useMoiaErrorNotification,
} from '@backoffice-frontend/patterns'
import { PersonArrowIcon } from '@moia-dev/pace-icons'
import { Button, LoadingIndicator } from '@moia-dev/pace-web'
import type { SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdVehicleStreamConfirmManualCheckInMutation } from './BoardingPassengers.hook'

export const CheckInPassengerDialog = ({
  tripId,
  vehicleId,
  isDialogOpen,
  setIsDialogOpen,
  onSuccess,
}: {
  tripId: string
  vehicleId: string
  isDialogOpen: boolean
  setIsDialogOpen: (isOpen: SetStateAction<boolean>) => void
  onSuccess?: () => void
}) => {
  const { t } = useTranslation()
  const { enqueueMoiaErrorNotification } = useMoiaErrorNotification()

  const [confirmManualCheckIn, { loading }] = useErrorHandlingMutation(
    useAdVehicleStreamConfirmManualCheckInMutation,
  )

  const checkInPassenger = async () => {
    const result = await confirmManualCheckIn({
      variables: {
        input: {
          tripId,
          fleetVehicle: vehicleId,
        },
      },
    })
    if (result.data?.manuallyCheckInPassenger?.error) {
      enqueueMoiaErrorNotification(
        result.data.manuallyCheckInPassenger.error.message,
      )
      return
    }
    onSuccess?.()
    setIsDialogOpen(false)
  }

  return (
    <MoiaDialog
      maxWidth="xs"
      open={isDialogOpen}
      onClose={() => setIsDialogOpen(false)}
      header={t('adFleetMap_dialog_cscRemoteCheckinConfirmation_header')}
    >
      <Divider />
      <MoiaDialogContent>
        <Body1>
          {t('adFleetMap_dialog_cscRemoteCheckinConfirmation_caption')}
        </Body1>
      </MoiaDialogContent>
      <MoiaDialogFormFooter
        trailing={
          <>
            <Button
              label={t('common_cancel_button')}
              variant="secondary"
              onClick={() => setIsDialogOpen(false)}
            />
            <Button
              variant="primary"
              label={t('adFleetMap_dialog_cscRemoteCheckinConfirmation_button')}
              icon={
                loading ? <LoadingIndicator size={20} /> : <PersonArrowIcon />
              }
              onClick={checkInPassenger}
              disabled={loading}
            />
          </>
        }
      />
    </MoiaDialog>
  )
}
